import { useMemo, useState } from "react"
import * as uuid from "uuid"
import * as httpx from "httpx"
import { CancellablePromise } from "real-cancellable-promise"
import * as api from "brandguard/api"
import { AssetType, UnifiedItem, unifiedItemPosition } from "./types"

export interface FanoutRequests {
	image: api.ImageSearchRequest
	text: api.TextSearchRequest
	document: api.DocumentSearchRequest
	video: api.VideoSearchRequest
}

export interface FanoutSearchers {
	image: (bid: string, req: api.ImageSearchRequest, ...o: httpx.option[]) => CancellablePromise<api.ImageSearchResponse>
	text: (bid: string, req: api.TextSearchRequest, ...o: httpx.option[]) => CancellablePromise<api.TextSearchResponse>
	document: (bid: string, req: api.DocumentSearchRequest, ...o: httpx.option[]) => CancellablePromise<api.DocumentSearchResponse>
	video: (bid: string, req: api.VideoSearchRequest, ...o: httpx.option[]) => CancellablePromise<api.VideoSearchResponse>
}

export const uploadsSearchers: FanoutSearchers = {
	image: api.images.uploads.search,
	text: api.text.uploads.search,
	document: api.documents.uploads.search,
	video: api.videos.uploads.search,
}

export const approvallogSearchers: FanoutSearchers = {
	image: api.images.approvallog.search,
	text: api.text.approvallog.search,
	document: api.documents.approvallog.search,
	video: api.videos.approvallog.search,
}

export interface FanoutResult {
	items: UnifiedItem[]
	next?: FanoutRequests
}

interface fanned {
	items: UnifiedItem[]
	more: boolean
}

type searchRequest = api.ImageSearchRequest | api.TextSearchRequest | api.DocumentSearchRequest | api.VideoSearchRequest

// fanoutSearch queries the per-asset-type endpoints in parallel and merges them
// into the newest `limit` items across types, in the created_at, id descending
// order the endpoints share. Each type's next request resumes after the last of
// its items on this page, so the page after holds the next newest items, and
// assets leaving or joining the results earlier in the list do not shift it.
// Types omitted from `types`, and exhausted types (a negative offset), are skipped.
export function fanoutSearch(
	bid: string,
	types: AssetType[],
	requests: FanoutRequests,
	searchers: FanoutSearchers,
	bearer: httpx.option,
): CancellablePromise<FanoutResult> {
	const wants = (t: AssetType): boolean => (types.length === 0 || types.includes(t)) && requests[t].offset >= 0n
	const fan = (t: AssetType, search: () => CancellablePromise<fanned>): CancellablePromise<fanned> =>
		wants(t) ? search() : CancellablePromise.resolve({ items: [], more: false })
	const more = (next?: searchRequest): boolean => Number(next?.offset ?? -1) >= 0

	return CancellablePromise.all([
		fan("image", () =>
			searchers.image(bid, requests.image, bearer).then((r) => ({
				items: (r.items ?? []).map((data) => ({ event_type: "image" as const, data })),
				more: more(r.next),
			})),
		),
		fan("text", () =>
			searchers.text(bid, requests.text, bearer).then((r) => ({
				items: (r.items ?? []).map((data) => ({ event_type: "text" as const, data })),
				more: more(r.next),
			})),
		),
		fan("document", () =>
			searchers.document(bid, requests.document, bearer).then((r) => ({
				items: (r.items ?? []).map((data) => ({ event_type: "document" as const, data })),
				more: more(r.next),
			})),
		),
		fan("video", () =>
			searchers.video(bid, requests.video, bearer).then((r) => ({
				items: (r.items ?? []).map((data) => ({ event_type: "video" as const, data })),
				more: more(r.next),
			})),
		),
	]).then(([image, text, document, video]) => {
		const lists = [image, text, document, video]
		const heads = lists.map(() => 0)
		const positions = lists.map((l) => l.items.map(unifiedItemPosition))
		const items: UnifiedItem[] = []
		const limit = Number(requests.image.limit)
		while (items.length < limit) {
			let pick = -1
			lists.forEach((l, i) => {
				if (heads[i] < l.items.length && (pick < 0 || positions[i][heads[i]] > positions[pick][heads[pick]])) pick = i
			})
			if (pick < 0) break
			items.push(lists[pick].items[heads[pick]++])
		}
		const next: FanoutRequests = {
			image: resume(requests.image, image, heads[0]),
			text: resume(requests.text, text, heads[1]),
			document: resume(requests.document, document, heads[2]),
			video: resume(requests.video, video, heads[3]),
		}
		return { items, next: Object.values(next).some((r) => r.offset >= 0n) ? next : undefined }
	})
}

// resume returns the request for the type's items after the first `consumed` of
// `l`, or an exhausted request (a negative offset) when none remain.
function resume<T extends searchRequest>(req: T, l: fanned, consumed: number): T {
	const last = l.items[consumed - 1]?.data.media
	if (consumed === l.items.length && !l.more) return { ...req, offset: -1n }
	if (!last) return req
	return { ...req, before: { created_at: last.created_at, id: last.id } }
}

// useFanoutPage holds the page of a fanout search, starting from `first`. Changing
// the filters or asset types produces a new key, which returns to the first page;
// the key also resets a pagination.Cursor's history.
export function useFanoutPage(
	first: FanoutRequests,
	types: AssetType[],
): [FanoutRequests, string, (next: FanoutRequests) => void] {
	const key = useMemo(() => uuid.v4(), [first, types.join(",")])
	const [page, setPage] = useState<{ key: string; requests: FanoutRequests } | undefined>(undefined)
	return [page?.key === key ? page.requests : first, key, (requests) => setPage({ key, requests })]
}
